import { useMemo, useState } from "react";
import { useKeyResults } from "@/hooks/useKeyResults";
import { PPP_LIST_LIMIT } from "@/hooks/usePpps";
import { KeyResult } from "@/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

interface KeyResultMultiSelectProps {
  value: string[];
  onChange: (pids: string[]) => void;
  disabled?: boolean;
}

const ALL_KRS = { limit: PPP_LIST_LIMIT, sortBy: "orderIndex", sortOrder: "ASC" } as const;

/** Picks the KRs a PPP contributes to, grouped by objective. Local search — the whole KR set fits one page. */
export function KeyResultMultiSelect({ value, onChange, disabled }: KeyResultMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useKeyResults(ALL_KRS);
  const krs = useMemo(() => data?.data ?? [], [data?.data]);

  const groups = useMemo(() => {
    const byObjective = new Map<string, { label: string; items: KeyResult[] }>();
    for (const kr of krs) {
      const key = kr.objective?.pid ?? "none";
      const label = kr.objective ? `${kr.objective.code} · ${kr.objective.name}` : "No objective";
      if (!byObjective.has(key)) byObjective.set(key, { label, items: [] });
      byObjective.get(key)!.items.push(kr);
    }
    return [...byObjective.values()];
  }, [krs]);

  const selected = krs.filter((kr) => value.includes(kr.pid));
  const toggle = (pid: string) =>
    onChange(value.includes(pid) ? value.filter((p) => p !== pid) : [...value, pid]);

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
              {value.length === 0 ? "Select key results…" : `${value.length} key result${value.length === 1 ? "" : "s"} linked`}
            </span>
            {isLoading ? <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by code or name…" />
            <CommandList className="max-h-72">
              <CommandEmpty>{isLoading ? "Loading…" : "No key results found."}</CommandEmpty>
              {groups.map((g) => (
                <CommandGroup key={g.label} heading={g.label}>
                  {g.items.map((kr) => (
                    <CommandItem key={kr.pid} value={`${kr.code} ${kr.name}`} onSelect={() => toggle(kr.pid)}>
                      <Check className={cn("mr-2 h-4 w-4", value.includes(kr.pid) ? "opacity-100" : "opacity-0")} />
                      <span className="mr-2 font-mono text-[10px] text-muted-foreground">{kr.code}</span>
                      <span className="truncate">{kr.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((kr) => (
            <Badge key={kr.pid} variant="secondary" className="gap-1 pr-1 font-normal" title={kr.name}>
              <span className="font-mono text-[10px]">{kr.code}</span>
              <span className="max-w-[160px] truncate text-xs">{kr.name}</span>
              <button
                type="button"
                aria-label={`Unlink ${kr.code}`}
                onClick={() => toggle(kr.pid)}
                disabled={disabled}
                className="rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
